// src/pages/MonteurStart.jsx
// Startseite: was steht an, was ist angefangen, was wartet auf das Büro.
//
// Der Monteur öffnet die App und weiß sofort, wo er hin muss — ohne Suchen,
// ohne Menü. Die nächste Wartung steht ganz oben, ein Knopf startet sie.

import { useEffect, useState } from 'react';
import { C } from './formui';
import { wartungenVorladen, vorabGeladeneAnzahl } from '../lib/vorabLaden';
import { vornameVon } from '../lib/personen';
import { useMeineWartungen, terminText as terminTextDe, stationText as stationTextDe, ortText,
         anlageText as anlageTextDe, hinweise as hinweiseDe } from '../hooks/useWartungen';
import { t, sprache } from '../lib/sprache';

// Die Text-Helfer liefern deutsche Texte. Hier nur fuer die Anzeige uebersetzt,
// die Helfer selbst bleiben unveraendert.
// Moegliche Tagesangaben: 'Heute', 'Morgen', 'Montag' ... 'Sonntag',
// 'ohne Termin' oder ein Datum in Zahlen (bleibt, wie es ist).
function terminText(datum, uhrzeit) {
  const de = terminTextDe(datum, uhrzeit);
  const m = String(de).match(/^(.*), (\d{1,2}:\d{2}) Uhr$/);
  const tag = t(m ? m[1] : de);
  return m ? t('{tag}, {zeit} Uhr', { tag, zeit: m[2] }) : tag;
}

function stationText(p) {
  const de = stationTextDe(p);
  return de === 'Station ohne Namen' ? t('Station ohne Namen') : de;
}

// "H1073 · begehbar · 1 Trafo · MS-Anlage SF6": Teil fuer Teil. Namen, die
// nicht im Woerterbuch stehen, kommen unveraendert zurueck.
function anlageText(p) {
  const de = anlageTextDe(p);
  if (!de) return de;
  return de.split(' · ').map(teil => {
    const n = teil.match(/^(\d+) Trafos$/);
    if (n) return t('{n} Trafos', { n: n[1] });
    return t(teil);
  }).join(' · ');
}

const hinweise = (p) => hinweiseDe(p).map(x => t(x));

function Abschnitt({ titel, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                    letterSpacing: 0.7, marginBottom: 10 }}>
        {titel}
      </div>
      {children}
    </div>
  );
}

function Chip({ children, art = 'neutral' }) {
  const farben = {
    neutral: { bg: C.gray100, fg: C.gray700 },
    gruen:   { bg: C.greenBg, fg: C.green },
    rot:     { bg: C.redBg, fg: C.red },
    gelb:    { bg: C.yellowBg, fg: C.navy },
  }[art];
  return (
    <span style={{ background: farben.bg, color: farben.fg, padding: '4px 10px', borderRadius: 6,
                   fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

// "Planung ändern" fuer das Buero. Ein eigener Knopf NEBEN der Karte - ein
// Knopf in einem Knopf ist in HTML nicht erlaubt und reagiert auf dem
// Telefon unzuverlaessig.
function PlanungAendernKnopf({ onClick, hell = false }) {
  return (
    <button type="button" onClick={onClick}
      style={{ minHeight: 40, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
               fontSize: 14, fontWeight: 700, fontFamily: 'inherit', touchAction: 'manipulation',
               background: hell ? 'transparent' : C.white,
               color: hell ? '#DCE5EE' : C.navy,
               border: '1.5px solid ' + (hell ? 'rgba(255,255,255,0.35)' : C.gray300) }}>
      {t('Planung ändern')}
    </button>
  );
}

// Die nächste Wartung, hervorgehoben
function NaechsteKarte({ p, onOeffnen, onPlanungAendern }) {
  const h = hinweise(p);
  return (
    <div style={{ background: C.navyMid, color: C.white, borderRadius: 14, padding: 18, marginBottom: 22,
                  boxShadow: '0 6px 22px rgba(10,22,40,0.22)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, letterSpacing: 0.9, textTransform: 'uppercase', color: C.gray300, fontWeight: 700 }}>
          {p.art === 'sichtpruefung' ? t('Nächste Sichtprüfung') : t('Nächste Wartung')}
        </span>
        <span style={{ background: C.green, color: C.white, borderRadius: 6, padding: '4px 10px',
                       fontSize: 13, fontWeight: 800 }}>
          {terminText(p.wartungs_datum, p.wartungs_uhrzeit)}
        </span>
      </div>

      <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.25, marginBottom: 5 }}>
        {p.stationen?.kunden?.name || p.kunde_name || stationText(p)}
      </div>
      <div style={{ fontSize: 15, color: C.onDark }}>{ortText(p)}</div>
      {anlageText(p) && (
        <div style={{ fontSize: 15, color: C.onDark, marginTop: 2 }}>{anlageText(p)}</div>
      )}

      {(h.length > 0 || p.plan_bemerkung) && (
        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 13px', marginTop: 14 }}>
          <div style={{ fontSize: 12, letterSpacing: 0.7, textTransform: 'uppercase', color: C.gray300,
                        fontWeight: 700, marginBottom: 8 }}>
            {t('Hinweis')}
          </div>
          {h.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: p.plan_bemerkung ? 8 : 0 }}>
              {h.map(x => (
                <span key={x} style={{ background: 'rgba(255,255,255,0.14)', color: '#DCE5EE',
                                       padding: '5px 11px', borderRadius: 6, fontSize: 14, fontWeight: 600 }}>
                  {x}
                </span>
              ))}
            </div>
          )}
          {p.plan_bemerkung && (
            <div style={{ fontSize: 14, color: C.onDark, fontStyle: 'italic', lineHeight: 1.45 }}>
              {p.plan_bemerkung}
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={() => onOeffnen(p.id)}
        style={{ width: '100%', marginTop: 16, minHeight: 56, background: C.green, color: C.white,
                 border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 800, cursor: 'pointer',
                 touchAction: 'manipulation' }}>
        {t('Wartung starten')}
      </button>
      {onPlanungAendern && (
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <PlanungAendernKnopf hell onClick={() => onPlanungAendern(p.id)}/>
        </div>
      )}
    </div>
  );
}

function Zeile({ p, onOeffnen, chips, onPlanungAendern }) {
  const h = hinweise(p);
  return (
    <div style={{ marginBottom: 10 }}>
    <button type="button" onClick={() => onOeffnen(p.id)}
      style={{ display: 'block', width: '100%', textAlign: 'left', background: C.white,
               border: '1.5px solid ' + C.gray200, borderRadius: 12, padding: '14px 15px',
               cursor: 'pointer', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline',
                    flexWrap: 'wrap', marginBottom: 3 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: C.navy, lineHeight: 1.25 }}>
          {p.stationen?.kunden?.name || p.kunde_name || stationText(p)}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.gray500, whiteSpace: 'nowrap' }}>
          {terminText(p.wartungs_datum, p.wartungs_uhrzeit)}
        </span>
      </div>
      <div style={{ fontSize: 14, color: C.gray700 }}>{ortText(p)}</div>
      {anlageText(p) && (
        <div style={{ fontSize: 14, color: C.gray500, marginTop: 1 }}>{anlageText(p)}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
        {chips}
        {h.map(x => <Chip key={x}>{x}</Chip>)}
      </div>
    </button>
    {onPlanungAendern && (
      <div style={{ marginTop: 6, textAlign: 'right' }}>
        <PlanungAendernKnopf onClick={() => onPlanungAendern(p.id)}/>
      </div>
    )}
    </div>
  );
}

export default function MonteurStart({ profile, userId, onOeffnenProtokoll, onNeueWartung, neueWartungText, onPlanungAendern }) {
  const istBuero = profile?.role === 'buero' || profile?.role === 'admin';
  const { anstehend, offen, wartetAufBuero, loading, error, reload } =
    useMeineWartungen(userId, { istBuero });

  const [naechste, ...weitere] = anstehend;

  // Vorschlag 11: Die naechsten Wartungen liegen auf dem Handy, bevor man ins
  // Funkloch faehrt - automatisch, sobald die Startseite mit Netz geladen hat.
  const [vorab, setVorab] = useState({ laeuft: false, anzahl: 0 });
  const kandidaten = [...offen, ...anstehend].map(p => p.id).join(',');
  useEffect(() => {
    if (loading || istBuero || !kandidaten) return undefined;
    let abgebrochen = false;
    // Die naechsten geplanten zuerst, dann angefangene - sonst verdraengten
    // alte angefangene die Wartungen der kommenden Tage.
    const liste = [...anstehend.slice(0, 3), ...offen.slice(0, 2), ...anstehend.slice(3), ...offen.slice(2)];
    setVorab({ laeuft: navigator.onLine, anzahl: vorabGeladeneAnzahl(liste) });
    if (!navigator.onLine) return undefined;
    wartungenVorladen(liste, { max: 5 })
      .catch(() => { /* still - beim naechsten Oeffnen erneut */ })
      .finally(() => { if (!abgebrochen) setVorab({ laeuft: false, anzahl: vorabGeladeneAnzahl(liste) }); });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, istBuero, kandidaten]);
  const vorname = vornameVon(profile, null);

  const grussZeit = () => {
    const h = new Date().getHours();
    if (h < 11) return t('Guten Morgen');
    if (h < 18) return t('Guten Tag');
    return t('Guten Abend');
  };

  const nichtsZuTun = !loading && anstehend.length === 0 && offen.length === 0 && wartetAufBuero.length === 0;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '18px 14px 40px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, color: C.gray500 }}>
          {new Date().toLocaleDateString(sprache() === 'hr' ? 'hr-HR' : 'de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.navy, letterSpacing: -0.3 }}>
          {grussZeit()}{vorname ? ', ' + vorname : ''}
        </div>
      </div>

      {!istBuero && (vorab.laeuft || vorab.anzahl > 0) && (
        <div style={{ marginTop: -10, marginBottom: 16, fontSize: 14, color: C.gray500 }}>
          {vorab.laeuft
            ? t('Wartungen werden für unterwegs gespeichert …')
            : t('📥 {n} Wartung(en) auch ohne Netz verfügbar', { n: vorab.anzahl })}
        </div>
      )}

      {/* Buero: "Wartung planen" gleich OBEN. Unten, unter einer langen Liste,
          wurde der Knopf nicht mehr gefunden (Anna-Lena 15.09.2026). */}
      {istBuero && onNeueWartung && (
        <button type="button" onClick={onNeueWartung}
          style={{ width: '100%', marginBottom: 18, minHeight: 52, background: C.navy,
                   border: 'none', borderRadius: 10, color: C.yellow,
                   fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          {neueWartungText || t('+ Wartung planen')}
        </button>
      )}

      {loading && (
        <div style={{ padding: 30, textAlign: 'center', color: C.gray500, fontSize: 15 }}>
          {t('Wartungen werden geladen …')}
        </div>
      )}

      {error && (
        <div style={{ background: C.redBg, border: '1.5px solid ' + C.red, borderRadius: 10,
                      padding: '14px 16px', marginBottom: 18, fontSize: 15, color: C.navy, lineHeight: 1.5 }}>
          <strong>{t('Die Wartungen konnten nicht geladen werden.')}</strong><br/>{t(error)}
          <button type="button" onClick={reload}
            style={{ display: 'block', marginTop: 10, minHeight: 44, padding: '10px 16px',
                     border: '1.5px solid ' + C.navy, borderRadius: 8, background: C.white,
                     color: C.navy, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            {t('Nochmal versuchen')}
          </button>
        </div>
      )}

      {naechste && <NaechsteKarte p={naechste} onOeffnen={onOeffnenProtokoll} onPlanungAendern={onPlanungAendern}/>}

      {weitere.length > 0 && (
        <Abschnitt titel={t('Danach geplant')}>
          {weitere.map(p => <Zeile key={p.id} p={p} onOeffnen={onOeffnenProtokoll} chips={<Chip>{t('geplant')}</Chip>} onPlanungAendern={onPlanungAendern}/>)}
        </Abschnitt>
      )}

      {offen.length > 0 && (
        <Abschnitt titel={t('Angefangen, noch nicht fertig')}>
          {offen.map(p => <Zeile key={p.id} p={p} onOeffnen={onOeffnenProtokoll} chips={<Chip art="gelb">{t('in Arbeit')}</Chip>}/>)}
        </Abschnitt>
      )}

      {wartetAufBuero.length > 0 && (
        <Abschnitt titel={istBuero ? t('Wartet auf deine Freigabe') : t('Abgeschlossen, wartet auf das Büro')}>
          {wartetAufBuero.map(p => (
            <Zeile key={p.id} p={p} onOeffnen={onOeffnenProtokoll} chips={<Chip art="gruen">{t('Monteur fertig')}</Chip>}/>
          ))}
        </Abschnitt>
      )}

      {nichtsZuTun && (
        <div style={{ background: C.white, border: '1.5px solid ' + C.gray200, borderRadius: 12,
                      padding: '26px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, marginBottom: 6 }}>
            {t('Keine Wartungen offen')}
          </div>
          <div style={{ fontSize: 15, color: C.gray700, lineHeight: 1.5 }}>
            {istBuero
              ? t('Sobald du eine Wartung planst, erscheint sie hier und beim zugewiesenen Monteur.')
              : t('Sobald das Büro dir eine Wartung zuweist, steht sie hier. Du kannst auch selbst eine anlegen.')}
          </div>
        </div>
      )}

      {onNeueWartung && !loading && !istBuero && (
        <button type="button" onClick={onNeueWartung}
          style={{ width: '100%', marginTop: 18, minHeight: 52, background: C.white,
                   border: '2px dashed ' + C.gray300, borderRadius: 10, color: C.navy,
                   fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          {neueWartungText || t('+ Wartung selbst anlegen')}
        </button>
      )}
    </div>
  );
}
